import type { HSV, HarmonyMode } from './types';

const wrapHue = (h: number): number => ((h % 360) + 360) % 360;

const clamp = (v: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, v));

const withHue = (base: HSV, offset: number): HSV => ({
  h: wrapHue(base.h + offset),
  s: base.s,
  v: base.v,
});

const withSV = (base: HSV, hueOffset: number, sMult: number, vMult: number): HSV => ({
  h: wrapHue(base.h + hueOffset),
  s: clamp(Math.round(base.s * sMult), 0, 100),
  v: clamp(Math.round(base.v * vMult), 5, 100),
});

// ─── Geometry Helpers ───

const OUTER_RATIO = 0.95;

/** Convert HSV to normalised x,y on the wheel (center=0, edge≈OUTER_RATIO). */
const hsvToNorm = (hsv: HSV): { x: number; y: number } => {
  const angle = ((hsv.h - 90) * Math.PI) / 180;
  const dist = (hsv.s / 100) * OUTER_RATIO;
  return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist };
};

/** Convert normalised x,y back to H and S. */
const normToHS = (x: number, y: number): { h: number; s: number } => {
  let angle = (Math.atan2(y, x) * 180) / Math.PI + 90;
  if (angle < 0) angle += 360;
  const dist = Math.sqrt(x * x + y * y);
  return { h: angle % 360, s: clamp(Math.round((dist / OUTER_RATIO) * 100), 0, 100) };
};

/** Project point P onto line segment A→B, return clamped parameter t and projected point. */
const projectOntoSegment = (
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): { t: number; x: number; y: number } => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { t: 0, x: ax, y: ay };
  const t = clamp(((px - ax) * dx + (py - ay) * dy) / lenSq, 0, 1);
  return { t, x: ax + t * dx, y: ay + t * dy };
};

/** Interpolate HSV linearly between two points. */
const lerpHSV = (a: HSV, b: HSV, t: number): HSV => {
  let dh = b.h - a.h;
  if (dh > 180) dh -= 360;
  if (dh < -180) dh += 360;
  return {
    h: wrapHue(a.h + dh * t),
    s: clamp(Math.round(a.s + (b.s - a.s) * t), 0, 100),
    v: clamp(Math.round(a.v + (b.v - a.v) * t), 5, 100),
  };
};

// ─── Harmony Mode Implementations ───

const analogous = (base: HSV, count: number): HSV[] => {
  if (count <= 1) return [base];
  const spread = 60;
  const deps = count - 1;
  const results: HSV[] = [base];
  for (let i = 0; i < deps; i++) {
    const step = spread / (deps + 1);
    const idx = i + 1;
    const sign = idx % 2 === 1 ? 1 : -1;
    const magnitude = Math.ceil(idx / 2) * step;
    results.push(withHue(base, sign * magnitude));
  }
  return results;
};

/**
 * MONO: index 0 = Base (B), last index = End (E).
 * E defaults to same H as B, lower S (toward center).
 * Intermediate handles are evenly spaced along B→E.
 */
const monochromatic = (base: HSV, count: number, monoEnd?: HSV): HSV[] => {
  if (count <= 1) return [base];
  const end: HSV = monoEnd ?? {
    h: base.h,
    s: clamp(Math.round(base.s * 0.2), 0, 100),
    v: clamp(Math.round(base.v * 0.4), 5, 100),
  };
  const results: HSV[] = [base];
  for (let i = 1; i < count - 1; i++) {
    const t = i / (count - 1);
    results.push(lerpHSV(base, end, t));
  }
  results.push(end);
  return results;
};

/**
 * TRIAD: First 3 = equilateral triangle (0°, 120°, 240°).
 * Extras placed on spokes (vertex↔center lines), round-robin across 3 spokes.
 */
const triad = (base: HSV, count: number): HSV[] => {
  const coreHues = [0, 120, 240];
  const core: HSV[] = coreHues.map((offset, i) =>
    i === 0 ? base : withHue(base, offset),
  );
  if (count <= 3) return core.slice(0, Math.max(count, 1));

  const results: HSV[] = [...core];
  results.push(...distributeExtrasOnSpokes(core, count - 3));
  return results;
};

/**
 * COMPLEMENT: index 0 = B, index 1 = C (180°).
 * Extras on the full B↔C diameter line.
 */
const complementary = (base: HSV, count: number): HSV[] => {
  const comp: HSV = withHue(base, 180);
  if (count <= 1) return [base];
  if (count === 2) return [base, comp];

  const results: HSV[] = [base, comp];
  const extras = count - 2;
  for (let i = 0; i < extras; i++) {
    // Distribute evenly along B↔C diameter line
    // Alternate between B-side and C-side, varying saturation
    const t = (i + 1) / (extras + 1);
    if (i % 2 === 0) {
      // C-side: same H as comp, varying S
      const s = clamp(Math.round(comp.s * (0.3 + t * 0.5)), 0, 100);
      results.push({ h: comp.h, s, v: comp.v });
    } else {
      // B-side: same H as base, varying S
      const s = clamp(Math.round(base.s * (0.3 + t * 0.5)), 0, 100);
      results.push({ h: base.h, s, v: base.v });
    }
  }
  return results;
};

/**
 * SPLIT: Always odd count. Base + mirror pairs around complement axis.
 */
const splitComplementary = (base: HSV, count: number): HSV[] => {
  const n = count % 2 === 0 ? count + 1 : count;
  if (n <= 1) return [base];

  const pairs = (n - 1) / 2;
  const results: HSV[] = [base];
  for (let i = 0; i < pairs; i++) {
    const offset = 30 + i * (120 / Math.max(pairs, 1));
    results.push(withHue(base, 180 - offset));
    results.push(withHue(base, 180 + offset));
  }
  return results;
};

/**
 * SQUARE: First 4 = square (0°, 90°, 180°, 270°).
 * Extras placed on spokes (vertex↔center lines), round-robin across 4 spokes.
 */
const square = (base: HSV, count: number): HSV[] => {
  const coreHues = [0, 90, 180, 270];
  const core: HSV[] = coreHues.map((offset, i) =>
    i === 0 ? base : withHue(base, offset),
  );
  if (count <= 4) return core.slice(0, Math.max(count, 1));

  const results: HSV[] = [...core];
  results.push(...distributeExtrasOnSpokes(core, count - 4));
  return results;
};

/**
 * COMPOUND: core = [0°, 30°, 180°(C), 150°, 210°].
 * Handle at 180° = C. Non-B/C handles move freely but follow B rotation.
 */
const compound = (base: HSV, count: number): HSV[] =>
  buildFromCoreHues(base, count, [0, 30, 180, 150, 210]);

const shades = (base: HSV, count: number): HSV[] => {
  if (count <= 1) return [base];
  const results: HSV[] = [base];
  const deps = count - 1;
  for (let i = 0; i < deps; i++) {
    const t = (i + 1) / count;
    const v = clamp(Math.round(base.v * (1 - t * 0.85)), 5, 100);
    results.push({ h: base.h, s: base.s, v });
  }
  return results;
};

const custom = (base: HSV, count: number): HSV[] => {
  if (count <= 1) return [base];
  const step = 360 / count;
  return Array.from({ length: count }, (_, i) =>
    i === 0 ? base : withHue(base, step * i),
  );
};

// ─── Shared Builders ───

/** Distribute extras on spokes (vertex↔center lines) for polygon modes. */
const distributeExtrasOnSpokes = (core: HSV[], extraCount: number): HSV[] => {
  const spokeCount = core.length;
  const results: HSV[] = [];
  // Round-robin distribute across spokes
  const perSpoke = Array(spokeCount).fill(0) as number[];
  for (let i = 0; i < extraCount; i++) {
    perSpoke[i % spokeCount]++;
  }
  for (let s = 0; s < spokeCount; s++) {
    const vertex = core[s];
    const n = perSpoke[s];
    for (let j = 0; j < n; j++) {
      // Place at varying saturation along the spoke (vertex→center)
      const t = (j + 1) / (n + 1);
      // t=0 at vertex, t=1 at center → reduce S
      const sat = clamp(Math.round(vertex.s * (1 - t * 0.7)), 0, 100);
      const val = clamp(Math.round(vertex.v * (1 - t * 0.3)), 5, 100);
      results.push({ h: vertex.h, s: sat, v: val });
    }
  }
  return results;
};

const buildFromCoreHues = (base: HSV, count: number, coreHues: number[]): HSV[] => {
  const results: HSV[] = [];
  const coreCount = Math.min(count, coreHues.length);
  for (let i = 0; i < coreCount; i++) {
    results.push(i === 0 ? base : withHue(base, coreHues[i]));
  }

  const extras = count - coreCount;
  const svVariants = [
    { sMult: 0.6, vMult: 1.25 },
    { sMult: 0.8, vMult: 0.7 },
    { sMult: 0.5, vMult: 1.1 },
    { sMult: 0.9, vMult: 0.5 },
    { sMult: 0.7, vMult: 0.85 },
    { sMult: 0.4, vMult: 1.15 },
  ];
  for (let i = 0; i < extras; i++) {
    const hueIdx = (i % Math.max(coreHues.length - 1, 1)) + 1;
    const varIdx = Math.floor(i / Math.max(coreHues.length - 1, 1));
    const sv = svVariants[varIdx % svVariants.length];
    results.push(withSV(base, coreHues[hueIdx % coreHues.length], sv.sMult, sv.vMult));
  }
  return results;
};

// ─── Harmony Function Registry ───

type HarmonyFn = (base: HSV, count: number, extra?: HSV) => HSV[];

const HARMONY_FNS: Record<string, HarmonyFn> = {
  analogous,
  monochromatic,
  triad,
  complementary,
  'split-complementary': splitComplementary,
  square,
  compound,
  shades,
  custom,
};

export const getHarmonyHandles = (mode: string, base: HSV, count: number, monoEnd?: HSV): HSV[] =>
  mode === 'monochromatic'
    ? monochromatic(base, count, monoEnd)
    : HARMONY_FNS[mode](base, count);

export const updateCustomHandle = (
  colors: HSV[],
  index: number,
  newHsv: HSV,
): HSV[] => colors.map((c, i) => (i === index ? newHsv : c));

// ─── Constrained Drag System ───

export type DragConstraint =
  | { type: 'rotate'; delta: number }
  | { type: 'none' }
  | { type: 'free'; hsv: HSV }
  | { type: 'mono-end'; hsv: HSV }
  | { type: 'mono-line'; hsv: HSV }
  | { type: 'spoke'; hsv: HSV }
  | { type: 'diameter-line'; hsv: HSV }
  | { type: 'mirror'; delta: number; mirrorIndex: number; s: number };

/** Core handle count for polygon modes */
const CORE_COUNTS: Partial<Record<HarmonyMode, number>> = {
  triad: 3,
  square: 4,
};

export const getConstrainedDrag = (
  mode: HarmonyMode,
  ruleColors: HSV[],
  handleIndex: number,
  newHS: { h: number; s: number },
): DragConstraint => {
  // ── CUSTOM: fully free ──
  if (mode === 'custom') {
    const existing = ruleColors[handleIndex];
    return { type: 'free', hsv: { h: newHS.h, s: newHS.s, v: existing?.v ?? 100 } };
  }

  // ── SHADES: no movement ──
  if (mode === 'shades') {
    return { type: 'none' };
  }

  // ── MONOCHROMATIC ──
  if (mode === 'monochromatic') {
    const lastIdx = ruleColors.length - 1;
    if (handleIndex === lastIdx) {
      // End handle: constrained to B↔center line (same H as B, varying S toward 0)
      const baseN = hsvToNorm(ruleColors[0]);
      const pointerN = hsvToNorm({ h: newHS.h, s: newHS.s, v: 100 });
      // Project onto segment from center(0,0) to base position
      const proj = projectOntoSegment(pointerN.x, pointerN.y, 0, 0, baseN.x, baseN.y);
      const hs = normToHS(proj.x, proj.y);
      const existing = ruleColors[handleIndex];
      return { type: 'mono-end', hsv: { h: hs.h, s: hs.s, v: existing?.v ?? 50 } };
    }
    // Middle handles: project onto B→E line, free slide
    const baseN = hsvToNorm(ruleColors[0]);
    const endN = hsvToNorm(ruleColors[lastIdx]);
    const pointerN = hsvToNorm({ h: newHS.h, s: newHS.s, v: 100 });
    const proj = projectOntoSegment(pointerN.x, pointerN.y, baseN.x, baseN.y, endN.x, endN.y);
    const hs = normToHS(proj.x, proj.y);
    const lineV = lerpHSV(ruleColors[0], ruleColors[lastIdx], proj.t);
    return { type: 'mono-line', hsv: { h: hs.h, s: hs.s, v: lineV.v } };
  }

  // ── TRIAD / SQUARE: core handles rotate, extras on spokes ──
  if (mode === 'triad' || mode === 'square') {
    const coreCount = CORE_COUNTS[mode]!;
    if (handleIndex < coreCount) {
      // Core handle: rotate entire group
      const ruleHue = ruleColors[handleIndex]?.h ?? 0;
      let delta = newHS.h - ruleHue;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      return { type: 'rotate', delta };
    }
    // Extra handle: constrain to nearest spoke (vertex↔center)
    const core = ruleColors.slice(0, coreCount);
    const pointerN = hsvToNorm({ h: newHS.h, s: newHS.s, v: 100 });

    let bestDist = Infinity;
    let bestProj = { x: 0, y: 0, t: 0 };
    let bestSpoke = 0;
    for (let s = 0; s < coreCount; s++) {
      const vertexN = hsvToNorm(core[s]);
      // Spoke from center(0,0) to vertex
      const proj = projectOntoSegment(pointerN.x, pointerN.y, 0, 0, vertexN.x, vertexN.y);
      const dx = pointerN.x - proj.x;
      const dy = pointerN.y - proj.y;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestProj = proj;
        bestSpoke = s;
      }
    }
    const hs = normToHS(bestProj.x, bestProj.y);
    const vertex = core[bestSpoke];
    // V interpolates from vertex.v at edge to a dimmer value at center
    const spokeV = clamp(Math.round(vertex.v * (0.5 + bestProj.t * 0.5)), 5, 100);
    return { type: 'spoke', hsv: { h: hs.h, s: hs.s, v: spokeV } };
  }

  // ── COMPLEMENTARY: C rotates pair, extras on full diameter line ──
  if (mode === 'complementary') {
    if (handleIndex === 1) {
      // C handle: rotate pair
      const ruleHue = ruleColors[1]?.h ?? 0;
      let delta = newHS.h - ruleHue;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      return { type: 'rotate', delta };
    }
    // Extra handles: constrain to full B↔C diameter line
    const baseN = hsvToNorm(ruleColors[0]);
    const compN = hsvToNorm(ruleColors[1]);
    const pointerN = hsvToNorm({ h: newHS.h, s: newHS.s, v: 100 });
    const proj = projectOntoSegment(pointerN.x, pointerN.y, baseN.x, baseN.y, compN.x, compN.y);
    const hs = normToHS(proj.x, proj.y);
    const lineV = lerpHSV(ruleColors[0], ruleColors[1], proj.t);
    return { type: 'diameter-line', hsv: { h: hs.h, s: hs.s, v: lineV.v } };
  }

  // ── SPLIT-COMPLEMENTARY: mirror pairs ──
  if (mode === 'split-complementary') {
    const mirrorIndex = handleIndex % 2 === 1 ? handleIndex + 1 : handleIndex - 1;
    if (mirrorIndex < 1 || mirrorIndex >= ruleColors.length) {
      return { type: 'none' };
    }
    const baseH = ruleColors[0]?.h ?? 0;
    const compAxis = wrapHue(baseH + 180);
    let delta = newHS.h - compAxis;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    return { type: 'mirror', delta, mirrorIndex, s: newHS.s };
  }

  // ── COMPOUND: B and C rotate, others free ──
  if (mode === 'compound') {
    const compIdx = getComplementIndex(mode, ruleColors);
    if (handleIndex === compIdx) {
      // C handle: rotate
      const ruleHue = ruleColors[handleIndex]?.h ?? 0;
      let delta = newHS.h - ruleHue;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      return { type: 'rotate', delta };
    }
    // Non-B/C handles: free movement
    const existing = ruleColors[handleIndex];
    return { type: 'free', hsv: { h: newHS.h, s: newHS.s, v: existing?.v ?? 100 } };
  }

  // ── ANALOGOUS: rotation (default) ──
  if (mode === 'analogous') {
    const ruleHue = ruleColors[handleIndex]?.h ?? 0;
    let delta = newHS.h - ruleHue;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    return { type: 'rotate', delta };
  }

  return { type: 'none' };
};

// ─── Helpers for labeling ───

/** Find the complement handle index for a given mode. Returns -1 if none. */
export const getComplementIndex = (mode: HarmonyMode, colors: HSV[]): number => {
  if (mode === 'complementary' && colors.length >= 2) return 1;
  if (mode === 'compound' && colors.length >= 3) return 2;
  return -1;
};

/** Get end (non-base core vertex) indices for labeling. */
export const getEndIndices = (mode: HarmonyMode, count: number): number[] => {
  if (mode === 'monochromatic' && count >= 2) return [count - 1];
  if (mode === 'triad') {
    const indices: number[] = [];
    if (count >= 2) indices.push(1);
    if (count >= 3) indices.push(2);
    return indices;
  }
  if (mode === 'square') {
    const indices: number[] = [];
    for (let i = 1; i < Math.min(count, 4); i++) indices.push(i);
    return indices;
  }
  return [];
};

/** Get the minimum handle count for a mode. */
export const getMinHandles = (mode: HarmonyMode): number => {
  if (mode === 'triad') return 3;
  if (mode === 'square') return 4;
  if (mode === 'split-complementary') return 3;
  return 2;
};

/** Enforce valid handle count per mode. */
export const enforceHandleCount = (mode: HarmonyMode, count: number, maximum = 10): number => {
  const min = getMinHandles(mode);
  const max = Math.max(min, Math.min(10, maximum));
  let n = Math.max(min, Math.min(max, count));
  if (mode === 'split-complementary' && n % 2 === 0) {
    n = n + 1 <= max ? n + 1 : n - 1;
  }
  return n;
};
